import { DEVICE_LOCK_KEY } from "@/session/types";

export type DeviceLockConfig = {
  id: string;
  username: string;
  createdAt: number;
};

function b64u(bytes: ArrayBuffer | Uint8Array): string {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let bin = "";
  u8.forEach((b) => {
    bin += String.fromCharCode(b);
  });
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function b64uDecode(s: string): Uint8Array {
  const b64 = String(s).replace(/-/g, "+").replace(/_/g, "/") + "===".slice((String(s).length + 3) % 4);
  const bin = atob(b64);
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

function randomBytes(n = 32): Uint8Array {
  const a = new Uint8Array(n);
  crypto.getRandomValues(a);
  return a;
}

export function deviceLockConfig(): DeviceLockConfig | null {
  try {
    return JSON.parse(localStorage.getItem(DEVICE_LOCK_KEY) || "null") as DeviceLockConfig | null;
  } catch {
    return null;
  }
}

export async function deviceLockAvailable(): Promise<boolean> {
  if (!window.PublicKeyCredential || !navigator.credentials?.create) return false;
  try {
    const check = PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable().catch(
      () => false
    );
    const timedOut = new Promise<boolean>((resolve) => setTimeout(() => resolve(false), 1200));
    return !!(await Promise.race([check, timedOut]));
  } catch {
    return false;
  }
}

export async function enableDeviceLock(username: string, displayName?: string): Promise<void> {
  if (!(await deviceLockAvailable())) throw new Error("DEVICE_LOCK_UNAVAILABLE");
  const cred = (await navigator.credentials.create({
    publicKey: {
      challenge: randomBytes(32),
      rp: { name: "Nanom Wallet" },
      user: {
        id: randomBytes(32),
        name: username,
        displayName: displayName || username,
      },
      pubKeyCredParams: [
        { type: "public-key", alg: -7 },
        { type: "public-key", alg: -257 },
      ],
      authenticatorSelection: {
        authenticatorAttachment: "platform",
        userVerification: "required",
      },
      timeout: 60000,
      attestation: "none",
    },
  })) as PublicKeyCredential | null;
  if (!cred) throw new Error("DEVICE_LOCK_CANCELLED");
  localStorage.setItem(
    DEVICE_LOCK_KEY,
    JSON.stringify({
      id: b64u(cred.rawId),
      username,
      createdAt: Date.now(),
    } satisfies DeviceLockConfig)
  );
}

export function disableDeviceLock(): void {
  localStorage.removeItem(DEVICE_LOCK_KEY);
}

export async function ensureDeviceUnlock(): Promise<boolean> {
  const cfg = deviceLockConfig();
  if (!cfg) return true;
  await navigator.credentials.get({
    publicKey: {
      challenge: randomBytes(32),
      allowCredentials: [{ type: "public-key", id: b64uDecode(cfg.id) }],
      userVerification: "required",
      timeout: 60000,
    },
  });
  return true;
}
